import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ViewTypeName } from "@air/api/types";
import { useBreakpointsContext } from "@air/provider-media-query";
import { memo, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { MobileToggleNestedItemsButton } from "~/_components/MobileToggleNestedItemsButton";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { PUBLIC_TOP_BAR_HEIGHT } from "~/components/PublicTopBar";
import { PublicSearchResultsNumber } from "~/components/Search/PublicSearchResultsNumber";
import { ViewControls } from "~/components/ViewControls/ViewControls";
import { ViewKanbanGroupByMenu } from "~/components/ViewControls/ViewKanbanGroupByMenu/ViewKanbanGroupByMenu";
import { ViewSortingMenu } from "~/components/ViewControls/ViewSortingMenu/ViewSortingMenu";
import { ViewTypeToggleMenu } from "~/components/ViewControls/ViewTypeToggleMenu/ViewTypeToggleMenu";
import { PublicViewVisibilityToggleMenu } from "~/components/ViewControls/ViewVisiblityToggleMenu/PublicViewVisibilityToggleMenu";
import { ViewControlsBoardHeader } from "~/components/ViewControlsBoardHeader";
import { ONE_HOUR } from "~/constants/unitsOfTime";
import { useIsSearchActive } from "~/hooks/search/useIsSearchActive";
import { usePublicHeaderHorizontalPadding } from "~/hooks/usePublicHeaderHorizontalPadding";
import { useRerenderOnMount } from "~/hooks/useRerenderOnMount";
import { centralizedBoardAncestorsSelector, centralizedBoardTitleSelector } from "~/store/centralizedBoard/selectors";
import { currentKanbanGroupByCustomFieldIdSelector, currentViewTypeNameSelector } from "~/store/configViews/selectors";
import { useGoToPublicBoard } from "~/swr-hooks/boards/useGoToPublicBoard";
import { useGetPublicCustomFieldOptions } from "~/swr-hooks/customFields/useGetPublicCustomFieldOptions";
import { canSeeBoardCustomFields } from "~/utils/permissions/boardPermissions";
export const PublicBoardViewControls = /*#__PURE__*/ memo((param)=>{
    let { scrollRef, headerRef } = param;
    const [isScrolledToTop, setIsScrolledToTop] = useState(false);
    const boardTitle = useSelector(centralizedBoardTitleSelector);
    const ancestors = useSelector(centralizedBoardAncestorsSelector);
    const { isSearchActive } = useIsSearchActive();
    const { goToPublicBoard } = useGoToPublicBoard();
    const onBoardClick = useCallback((board)=>goToPublicBoard(board, "breadcrumb"), [
        goToPublicBoard
    ]);
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);
    const inGalleryView = currentViewTypeName === ViewTypeName.gallery;
    const inKanbanView = currentViewTypeName === ViewTypeName.kanban;
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { isMounted } = useRerenderOnMount();
    const { permissions } = usePublicPermissions();
    const canViewCustomFields = canSeeBoardCustomFields(permissions);
    const { responsiveHorizontalPadding } = usePublicHeaderHorizontalPadding();
    const customFieldId = useSelector(currentKanbanGroupByCustomFieldIdSelector);
    const { data: options } = useGetPublicCustomFieldOptions({
        customFieldId,
        staleTime: ONE_HOUR
    });
    const header = useMemo(()=>{
        /**
     * We need to check if mounted here because on the server side, isActive is false but on first render,
     * if the user has a search query param in the url, isActive will be true so this check prevents
     * a rehydration error
     *  */ if (isSearchActive && isMounted) {
            return /*#__PURE__*/ _jsx(PublicSearchResultsNumber, {});
        } else if (isScrolledToTop && boardTitle) {
            return /*#__PURE__*/ _jsx(ViewControlsBoardHeader, {
                title: boardTitle,
                ancestors: ancestors,
                onBoardClick: onBoardClick
            });
        } else if (!isAboveMediumScreen && (inGalleryView || inKanbanView)) {
            return /*#__PURE__*/ _jsx(ViewSortingMenu, {});
        }
        return null;
    }, [
        ancestors,
        boardTitle,
        inGalleryView,
        inKanbanView,
        isAboveMediumScreen,
        isMounted,
        isScrolledToTop,
        isSearchActive,
        onBoardClick
    ]);
    const buttons = useMemo(()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                inKanbanView && /*#__PURE__*/ _jsx("div", {
                    className: "mx-1",
                    children: canViewCustomFields && /*#__PURE__*/ _jsx(ViewKanbanGroupByMenu, {
                        customFieldValues: options === null || options === void 0 ? void 0 : options.data
                    })
                }),
                isAboveMediumScreen && (inGalleryView || inKanbanView) && /*#__PURE__*/ _jsx("div", {
                    className: "mx-1",
                    children: /*#__PURE__*/ _jsx(ViewSortingMenu, {})
                }),
                !isAboveMediumScreen && /*#__PURE__*/ _jsx(MobileToggleNestedItemsButton, {}),
                isAboveMediumScreen && /*#__PURE__*/ _jsx("div", {
                    className: "mx-1",
                    children: /*#__PURE__*/ _jsx(PublicViewVisibilityToggleMenu, {})
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "ml-1",
                    children: /*#__PURE__*/ _jsx(ViewTypeToggleMenu, {
                        canViewCustomFields: canViewCustomFields
                    })
                })
            ]
        }), [
        canViewCustomFields,
        inGalleryView,
        inKanbanView,
        isAboveMediumScreen,
        options === null || options === void 0 ? void 0 : options.data
    ]);
    const style = useMemo(()=>({
            paddingLeft: responsiveHorizontalPadding,
            paddingRight: responsiveHorizontalPadding
        }), [
        responsiveHorizontalPadding
    ]);
    return /*#__PURE__*/ _jsx(ViewControls, {
        headerRef: headerRef,
        scrollRef: scrollRef,
        buttons: buttons,
        header: header,
        onScrolled: setIsScrolledToTop,
        style: style,
        topPosition: PUBLIC_TOP_BAR_HEIGHT
    });
});
PublicBoardViewControls.displayName = "PublicBoardViewControls";
